import { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from 'react-query';
import { LocalizationContext, UserContext } from '../contexts';
import { stringScoreNormalizer, getLangAndWorkspace } from '../utils/helper';
import { languageWorkspaceOptions } from '../config';

export const useBanners = () => {
    // Store the combo ID (e.g., 'he_ofek') instead of just lang
    const [lang, setLang] = useState(languageWorkspaceOptions[0].id);
    const [userType, setUserType] = useState('teacher');

    const [bannersList, setBannersList] = useState([]);
    const [loadingBannersList, setLoadingBannersList] = useState(false);
    const [bannersListLoadingError, setBannersListLoadingError] = useState(null);

    const [editItemId, setEditItemId] = useState(0);
    const [isEditItemLoading, setIsEditItemLoading] = useState(false);
    const [errorEditItemLoading, setErrorEditItemLoading] = useState(null);
    const [editItem, setEditItem] = useState(null);

    const [isItemSaving, setIsItemSaving] = useState(false);
    const [saveItemError, setSaveItemError] = useState(null);

    const [isItemDeleting, setIsItemDeleting] = useState(false);
    const [itemDeletingError, setItemDeletingError] = useState(null);

    const [disciplines, setDisciplines] = useState([]);
    const [ageGrades, setAgeGrades] = useState([]);
    const [sectors, setSectors] = useState([]);

    const history = useHistory();
    const [localization] = useContext(LocalizationContext);
    const [user] = useContext(UserContext);

    const [loadingDimensionsError, setLoadingDimensionsError] = useState(null);
    const { isLoading, isError, data, error } = useQuery('repository_dimensions', () =>
        window.cet.microservices.contentapi.content.getAllDimensionsAndDependencies({}),
        {
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            refetchOnMount: false
        }
    );

    useEffect(() => {
        const dimensions = data && data.dimensions;
        if (dimensions && dimensions.disciplines) {
            var newDisciplines = [];
            Object.keys(dimensions.disciplines).forEach(key => {
                newDisciplines.push(dimensions.disciplines[key]);
            });
            setDisciplines(newDisciplines);
        }
        else {
            setDisciplines([]);
        }

        if (dimensions && dimensions.sectors) {
            var newSectors = [];
            Object.keys(dimensions.sectors).forEach(key => {
                newSectors.push(dimensions.sectors[key]);
            });
            setSectors(newSectors);
        }
        else {
            setSectors([]);
        }

        if (dimensions && dimensions.ageGrades) {
            var newAgeGrades = [];
            Object.keys(dimensions.ageGrades).forEach(key => {
                newAgeGrades.push(dimensions.ageGrades[key]);
            });
            setAgeGrades(newAgeGrades);
        }
        else {
            setAgeGrades([]);
        }
    }, [data]);

    useEffect(() => {
        setLoadingDimensionsError(error || null);
    }, [error]);

    useEffect(() => {
        setLoadingBannersList(true);

        // Extract lang and workspace from combo ID
        const { lang: apiLang, workspace } = getLangAndWorkspace(lang);

        window.cet.microservices.nairobiapi.banners.getBanners([{
            langs: apiLang ? [apiLang] : [],
            workspace: workspace,
            userTypes: userType ? [userType] : [],
        }]).then(items => {
            let _items = [...items] || [];
            _items.forEach(item => {
                try {
                    item.content = JSON.parse(item.content);
                }
                catch (ex) {
                    item.content = {};
                }
                if (item.lastUpdatedDate) {
                    item.lastUpdatedDate = new Date(item.lastUpdatedDate);
                }
                item.title = item.content.name ? item.content.name + '<!--###-->' + stringScoreNormalizer(item.content.name) : '';
                item.type = localization.labels.bannersManagement.searchResult.types[item.bannerType + '_' + (item.content.type || 'image')];
            });
            setBannersList(_items);
            setLoadingBannersList(false);
            setBannersListLoadingError(null);
        }).catch(error => {
            setBannersList([]);
            setLoadingBannersList(false);
            setBannersListLoadingError(error);
        });
    }, [lang, userType]);

    useEffect(() => {
        let isActive = true;

        // Extract lang and workspace from combo ID
        const { lang: apiLang, workspace } = getLangAndWorkspace(lang);

        if(editItemId === 'new') {
            setEditItem({
                id: 0,
                isPublish: false,
                lang: apiLang,
                workspace: workspace,
                userType: userType,
                bannerType: "TextImage",
                pages: [],
                ageGrades: [],
                disciplines: [],
                sectors: [],
                content: {},
                lastUpdatedDate: null,
                lastUpdatedUser: ''
            });
            setIsEditItemLoading(false);
            setErrorEditItemLoading(null);
        }
        else if(editItemId) {
            window.cet.microservices.nairobiapi.banners.getBanners([{
                ids: [editItemId],
                workspace: workspace
            }]).then(items => {
                if(isActive) {
                    let item = items && items.length > 0 ? items[0] : null;
                    if(item) {
                        try {
                            item.content = JSON.parse(item.content);
                        }
                        catch (ex) {
                            item.content = {};
                        }
                    }
                    setEditItem(items && items.length > 0 ? items[0] : null);
                    setErrorEditItemLoading(null);
                    setIsEditItemLoading(false);
                }
            }).catch(error => {
                if(isActive) {
                    setEditItem(null);
                    setErrorEditItemLoading(error);
                    setIsEditItemLoading(false);
                }
            });
        }
        else {
            setEditItem(null);
            setErrorEditItemLoading(null);
            setIsEditItemLoading(false);
        }

        return () => {
            isActive = false;
        };
    }, [editItemId]);

    const saveItem = (editItem) => {
        const promise = new window.Promise((resolve, reject) => {
            setIsItemSaving(true);
            setSaveItemError(null);
            if(editItem) {
                let item = {...editItem};
                if (user && user.displayName) {
                    item.lastUpdatedUser = user.displayName;
                }
                item.content = item.content ? JSON.stringify(item.content) : "{}";
                
                // Get workspace from the current lang combo or from item's stored workspace
                const { workspace } = getLangAndWorkspace(lang);
                
                window.cet.microservices.nairobiapi.banners.setBanner({...item, workspace: item.workspace || workspace}).then(item => {
                    try {
                        item.content = JSON.parse(item.content);
                    }
                    catch (ex) {
                        item.content = {};
                    }
                    setEditItem(item);
                    setIsItemSaving(false);
                    setSaveItemError(null);
                    resolve(item);
                }).catch(error => {
                    setIsItemSaving(false);
                    setSaveItemError(error);
                    reject(error);
                });
            }
            else {
                resolve(null);
                setIsItemSaving(false);
                setSaveItemError(null);
            }
        });

        promise.then(item => {
            if(item && bannersList) {
                let newItem = {...item};
                if (newItem.lastUpdatedDate) {
                    newItem.lastUpdatedDate = new Date(newItem.lastUpdatedDate);
                }
                newItem.title = newItem.content.name ? newItem.content.name + '<!--###-->' + stringScoreNormalizer(newItem.content.name) : '';
                newItem.type = localization.labels.bannersManagement.searchResult.types[newItem.bannerType + '_' + (newItem.content.type || 'image')];

                const { lang: apiLang } = getLangAndWorkspace(lang);
                const itemInList = bannersList && bannersList.length > 0 && bannersList.filter(banner => banner.id === newItem.id).length > 0;
                const isMastByInList = apiLang === newItem.lang && userType === newItem.userType;

                if (!isMastByInList && !itemInList) {

                }
                else if(isMastByInList && !itemInList) {
                    setBannersList([newItem, ...bannersList]);
                }
                else if (!isMastByInList && itemInList) {
                    setBannersList(([...bannersList]).filter(banner => banner.id !== newItem.id));
                }
                else if (isMastByInList && itemInList) {
                    setBannersList(([...bannersList]).map(banner => {
                        if (banner.id === newItem.id) {
                            return newItem;
                        }
                        else {
                            return banner;
                        }
                    }));
                }

                return newItem;
            }

            return item;
        });

        return promise;
    };

    const deleteItem = () => {
        if(editItem && editItem.id) {
            setIsItemDeleting(true);
            setItemDeletingError(null);

            // Get workspace from the current lang combo or from editItem's stored workspace
            const { workspace } = getLangAndWorkspace(lang);

            window.cet.microservices.nairobiapi.banners.deleteBanner({
                id: editItem.id,
                workspace: editItem.workspace || workspace
            }).then(() => {
                if(bannersList) {
                    setBannersList(([...bannersList]).filter(banner => banner.id !== editItem.id));
                }
                setEditItem(null);
                setIsItemDeleting(false);
                setItemDeletingError(null);
                history.push('/internalPages');
            }).catch(error => {
                setIsItemDeleting(false);
                setItemDeletingError(error);
            });
        }
    };

    const clearApisErrors = () => {
        setLoadingDimensionsError(null); 
        setBannersListLoadingError(null);
        setErrorEditItemLoading(null);
        setItemDeletingError(null);
        setSaveItemError(null);
    };

    const _setLang = (comboId) => {
        setLang(comboId);
    }
    const _setUserType = (userType) => {
        setUserType(userType)
    };
    const _setEditItemId = (id) => {
        setEditItemId(id);
    };

    return {
        lang: lang,
        setLang: _setLang,
        userType: userType,
        setUserType: _setUserType,

        loadingDimensions: isLoading,
        loadingDimensionsError: loadingDimensionsError,
        disciplines: disciplines,
        ageGrades: ageGrades,
        sectors: sectors,

        bannersList: bannersList,
        loadingBannersList: loadingBannersList,
        bannersListLoadingError: bannersListLoadingError,

        setEditItemId: _setEditItemId,
        isEditItemLoading: isEditItemLoading,
        errorEditItemLoading: errorEditItemLoading,
        editItem: editItem,

        saveItem: saveItem,
        isItemSaving: isItemSaving,
        saveItemError: saveItemError,

        deleteItem: deleteItem,
        isItemDeleting: isItemDeleting,
        itemDeletingError: itemDeletingError,

        clearApisErrors: clearApisErrors
    }
};
